/**
 * EditorShell - Standardized layout for advanced editor panels.
 * Provides consistent header, scrollable content, and footer structure.
 */
import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";

interface EditorShellProps {
	/** Title displayed in the header */
	title: string;
	/** Optional description below the title */
	description?: string;
	/** Callback when back button is clicked */
	onBack: () => void;
	/** Optional actions rendered in the footer (Cancel, Save buttons) */
	footerActions?: ReactNode;
	/** The main content of the editor */
	children: ReactNode;
	/** Whether to constrain content width (default: true) */
	constrainWidth?: boolean;
}

/**
 * A standardized shell for editor interfaces within sliding panels.
 * Provides:
 * - Fixed header with back button and title
 * - Scrollable content area with optional max-width constraint
 * - Sticky footer for actions
 * @param props - Component props
 * @param props.title - Title displayed in the header
 * @param props.description - Optional description below the title
 * @param props.onBack - Callback when back button is clicked
 * @param props.footerActions - Optional actions rendered in the footer
 * @param props.children - The main content of the editor
 * @param props.constrainWidth - Whether to constrain content width (default: true)
 */
export function EditorShell({
	title,
	description,
	onBack,
	footerActions,
	children,
	constrainWidth = true,
}: EditorShellProps) {
	return (
		<div className="flex flex-col h-full w-full bg-background">
			{/* Header */}
			<header className="flex-none border-b px-6 py-4 flex items-center gap-4">
				<Button variant="ghost" size="icon" onClick={onBack}>
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<div>
					<h2 className="text-lg font-semibold">{title}</h2>
					{description && (
						<p className="text-sm text-muted-foreground">{description}</p>
					)}
				</div>
			</header>

			{/* Scrollable Body */}
			<div className="flex-1 overflow-y-auto p-6">
				{constrainWidth ? (
					<div className="max-w-4xl mx-auto">{children}</div>
				) : (
					children
				)}
			</div>

			{/* Footer */}
			{footerActions && (
				<footer className="flex-none border-t px-6 py-4 flex justify-end gap-3">
					{footerActions}
				</footer>
			)}
		</div>
	);
}
